import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Variantes du composant Button
 * Design premium avec dégradés, ombres et animations
 */
const buttonVariants = cva(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-semibold ring-offset-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-body",
    {
        variants: {
            variant: {
                default:
                    "bg-accent-gradient text-white shadow-soft hover:shadow-glow hover:-translate-y-0.5 active:translate-y-0",
                secondary:
                    "bg-navy-800 text-white shadow-soft hover:bg-navy-700 hover:-translate-y-0.5",
                outline:
                    "border-2 border-navy-200 bg-white text-navy-800 hover:bg-navy-50 hover:border-navy-300",
                ghost:
                    "text-navy-600 hover:bg-navy-50 hover:text-navy-800",
                link:
                    "text-accent-500 underline-offset-4 hover:underline",
                white:
                    "bg-white text-navy-800 shadow-soft hover:shadow-soft-lg hover:-translate-y-0.5",
            },
            size: {
                default: "h-11 px-6 py-2",
                sm: "h-9 px-4 text-xs",
                lg: "h-13 px-8 py-3 text-base",
                xl: "h-14 px-10 py-4 text-lg",
                icon: "h-10 w-10",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

/**
 * Composant Button premium avec variants, tailles et animations
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button";

export { Button, buttonVariants };
